import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  CheckCircle, 
  Circle, 
  FileText, 
  Video, 
  Monitor,
  Users,
  Star,
  Target,
  Clock,
  AlertCircle,
  BookOpen,
  PenTool,
  Sparkles
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ComponentCoachProps {
  componentId: string;
  onClose: () => void;
}

export const ComponentCoach: React.FC<ComponentCoachProps> = ({ componentId, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [responses, setResponses] = useState<{[key: string]: string}>({});
  const [aiFeedback, setAiFeedback] = useState<{[key: string]: string}>({});
  const [isGeneratingFeedback, setIsGeneratingFeedback] = useState(false);

  const componentData = {
    component1: {
      title: 'Component 1: Content Knowledge',
      description: 'Computer-based assessment of content knowledge and pedagogical practices',
      icon: <Monitor className="h-8 w-8" />,
      duration: '3 hours',
      type: 'Computer-Based Assessment',
      steps: [
        {
          title: 'Understand the Assessment Format',
          description: 'Learn about the structure and expectations',
          tasks: [
            'Review assessment overview',
            'Understand time limits and format',
            'Practice with sample questions'
          ]
        },
        {
          title: 'Content Knowledge Preparation',
          description: 'Review and strengthen your subject matter expertise',
          tasks: [
            'Review your certificate area standards',
            'Study developmental considerations',
            'Practice pedagogical content knowledge'
          ]
        },
        {
          title: 'Constructed Response Practice',
          description: 'Master the three constructed response exercises',
          tasks: [
            'Analyze sample responses',
            'Practice writing clear, concise answers',
            'Time yourself on practice questions'
          ]
        },
        {
          title: 'Selected Response Mastery',
          description: 'Prepare for the 45 multiple choice questions',
          tasks: [
            'Review question types and formats',
            'Practice elimination strategies',
            'Study common misconceptions'
          ]
        },
        {
          title: 'Final Preparation',
          description: 'Get ready for assessment day',
          tasks: [
            'Complete full practice assessment',
            'Review technical requirements',
            'Plan your assessment day schedule'
          ]
        }
      ]
    },
    component2: {
      title: 'Component 2: Differentiation in Instruction',
      description: 'Portfolio entry analyzing student needs and instructional differentiation',
      icon: <Users className="h-8 w-8" />,
      duration: '2-3 months',
      type: 'Portfolio Entry',
      steps: [
        {
          title: 'Student Analysis and Selection',
          description: 'Choose and analyze your focus students',
          tasks: [
            'Select 3-4 focus students with diverse needs',
            'Gather baseline assessment data',
            'Document student backgrounds and interests'
          ]
        },
        {
          title: 'Instructional Planning',
          description: 'Design differentiated instruction',
          tasks: [
            'Create learning objectives',
            'Plan differentiated activities',
            'Design varied assessment strategies'
          ]
        },
        {
          title: 'Implementation and Documentation',
          description: 'Teach and collect evidence',
          tasks: [
            'Implement differentiated lessons',
            'Collect student work samples',
            'Document instructional adjustments'
          ]
        },
        {
          title: 'Analysis and Reflection',
          description: 'Analyze student growth and your teaching',
          tasks: [
            'Analyze student work for growth',
            'Reflect on instructional effectiveness',
            'Plan next steps for learning'
          ]
        },
        {
          title: 'Written Commentary',
          description: 'Complete your comprehensive analysis',
          tasks: [
            'Write detailed student analyses',
            'Explain instructional choices',
            'Reflect on student outcomes'
          ]
        }
      ]
    },
    component3: {
      title: 'Component 3: Teaching Practice and Learning Environment',
      description: 'Video-based portfolio showing classroom interactions and teaching',
      icon: <Video className="h-8 w-8" />,
      duration: '2-3 months',
      type: 'Video Portfolio',
      steps: [
        {
          title: 'Video Planning and Preparation',
          description: 'Plan your video submissions',
          tasks: [
            'Review video requirements',
            'Plan lessons for recording',
            'Prepare consent forms and logistics'
          ]
        },
        {
          title: 'Recording Setup',
          description: 'Set up for high-quality recordings',
          tasks: [
            'Test recording equipment',
            'Plan camera positioning',
            'Practice with recording technology'
          ]
        },
        {
          title: 'Lesson Implementation',
          description: 'Teach and record your lessons',
          tasks: [
            'Record multiple lesson segments',
            'Capture student interactions',
            'Document learning environment'
          ]
        },
        {
          title: 'Video Selection and Editing',
          description: 'Choose and prepare your best footage',
          tasks: [
            'Review all recordings',
            'Select strongest segments',
            'Edit to meet time requirements'
          ]
        },
        {
          title: 'Written Commentary',
          description: 'Analyze your teaching practice',
          tasks: [
            'Describe teaching context',
            'Analyze student engagement',
            'Reflect on instructional decisions'
          ]
        }
      ]
    },
    component4: {
      title: 'Component 4: Effective and Reflective Practitioner',
      description: 'Portfolio demonstrating professional growth and collaboration',
      icon: <Star className="h-8 w-8" />,
      duration: '2-3 months',
      type: 'Portfolio Entry',
      steps: [
        {
          title: 'Student Knowledge Application',
          description: 'Demonstrate knowledge of your students',
          tasks: [
            'Document student backgrounds',
            'Show knowledge of learning needs',
            'Connect to instructional planning'
          ]
        },
        {
          title: 'Assessment and Planning',
          description: 'Show effective use of assessment data',
          tasks: [
            'Design formative assessments',
            'Analyze assessment results',
            'Plan responsive instruction'
          ]
        },
        {
          title: 'Professional Collaboration',
          description: 'Document collaborative efforts',
          tasks: [
            'Work with colleagues',
            'Engage families and community',
            'Participate in professional learning'
          ]
        },
        {
          title: 'Reflection and Growth',
          description: 'Demonstrate reflective practice',
          tasks: [
            'Analyze teaching effectiveness',
            'Plan professional development',
            'Set growth goals'
          ]
        },
        {
          title: 'Portfolio Completion',
          description: 'Finalize your submission',
          tasks: [
            'Complete written commentary',
            'Organize supporting evidence',
            'Review submission requirements'
          ]
        }
      ]
    }
  };

  const component = componentData[componentId as keyof typeof componentData];

  if (!component) {
    return <div>Component not found</div>;
  }

  const handleStepComplete = (stepIndex: number) => {
    if (!completedSteps.includes(stepIndex)) {
      setCompletedSteps([...completedSteps, stepIndex]);
    }
  };

  const handleResponseChange = (key: string, value: string) => {
    setResponses(prev => ({ ...prev, [key]: value }));
  };

  const generateAIFeedback = async (stepIndex: number) => {
    const responseKey = `step-${stepIndex}`;
    const responseText = responses[responseKey];

    if (!responseText?.trim()) {
      toast.error('Please enter some reflection notes before requesting feedback');
      return;
    }

    setIsGeneratingFeedback(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-insights', {
        body: {
          content: responseText,
          context: `NBCT ${component.title} - Step ${stepIndex + 1}: ${component.steps[stepIndex].title}`,
          type: 'nbct_reflection_feedback'
        }
      });

      if (error) throw error;
      
      const feedback = data.insights || 'Unable to generate feedback at this time.';
      setAiFeedback(prev => ({ ...prev, [`feedback-${stepIndex}`]: feedback }));
      toast.success('AI feedback generated!');
    } catch (error) {
      console.error('Error generating AI feedback:', error);
      toast.error('Failed to generate AI feedback');
    } finally {
      setIsGeneratingFeedback(false);
    }
  };

  const progress = (completedSteps.length / component.steps.length) * 100;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="p-3 rounded-lg bg-primary text-primary-foreground">
            {component.icon}
          </div>
          <div>
            <h2 className="text-2xl font-bold">{component.title}</h2>
            <p className="text-muted-foreground">{component.description}</p>
          </div>
        </div>
        <Button variant="outline" onClick={onClose}>
          Back to Overview
        </Button>
      </div>

      {/* Progress Card */}
      <Card>
        <CardHeader>
          <CardTitle>Your Progress</CardTitle>
          <CardDescription>
            {completedSteps.length} of {component.steps.length} steps completed
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Progress value={progress} className="h-3" />
          <div className="flex justify-between text-sm text-muted-foreground mt-2">
            <span>{progress.toFixed(0)}% Complete</span>
            <span>Estimated Time: {component.duration}</span>
          </div>
        </CardContent>
      </Card>

      {/* Component Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Component Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <Badge variant="secondary">{component.type}</Badge>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{component.duration}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Target className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Portfolio Evidence</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Steps */}
      <Card>
        <CardHeader>
          <CardTitle>Guided Steps</CardTitle>
          <CardDescription>Follow these steps to complete your component</CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible value={`step-${currentStep}`}>
            {component.steps.map((step, index) => (
              <AccordionItem key={index} value={`step-${index}`}>
                <AccordionTrigger 
                  className="hover:no-underline"
                  onClick={() => setCurrentStep(index)}
                >
                  <div className="flex items-center space-x-3">
                    {completedSteps.includes(index) ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground" />
                    )}
                    <div className="text-left">
                      <div className="font-medium">Step {index + 1}: {step.title}</div>
                      <div className="text-sm text-muted-foreground">{step.description}</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-4">
                    <div className="grid gap-3">
                      {step.tasks.map((task, taskIndex) => (
                        <div key={taskIndex} className="flex items-start space-x-3 p-3 rounded-lg bg-muted/50">
                          <Circle className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <span className="text-sm">{task}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="space-y-4 border-t pt-4">
                      <Label htmlFor={`reflection-${index}`}>Reflection Notes</Label>
                      <Textarea
                        id={`reflection-${index}`}
                        placeholder="Document your progress, insights, and questions for this step..."
                        value={responses[`step-${index}`] || ''}
                        onChange={(e) => handleResponseChange(`step-${index}`, e.target.value)}
                        rows={4}
                      />
                      
                      <Button
                        variant="outline"
                        onClick={() => generateAIFeedback(index)}
                        disabled={isGeneratingFeedback || !responses[`step-${index}`]?.trim()}
                        className="w-full"
                      >
                        <Sparkles className="h-4 w-4 mr-2" />
                        {isGeneratingFeedback ? 'Getting AI Feedback...' : 'Get AI Feedback on Reflection'}
                      </Button>

                      {aiFeedback[`feedback-${index}`] && (
                        <Card className="bg-blue-50 border-blue-200">
                          <CardHeader>
                            <CardTitle className="text-blue-800 text-lg flex items-center gap-2">
                              <Sparkles className="h-5 w-5" />
                              AI Coaching Feedback
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-blue-700 whitespace-pre-wrap text-sm">
                              {aiFeedback[`feedback-${index}`]}
                            </p>
                          </CardContent>
                        </Card>
                      )}
                    </div>

                    <div className="flex space-x-2">
                      <Button 
                        onClick={() => handleStepComplete(index)}
                        disabled={completedSteps.includes(index)}
                      >
                        {completedSteps.includes(index) ? (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Completed
                          </>
                        ) : (
                          'Mark Complete'
                        )}
                      </Button>
                      {index < component.steps.length - 1 && (
                        <Button 
                          variant="outline" 
                          onClick={() => setCurrentStep(index + 1)}
                        >
                          Next Step
                        </Button>
                      )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* Resources and Tips */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BookOpen className="h-5 w-5" />
              <span>Key Resources</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button variant="outline" className="w-full justify-start">
              <FileText className="h-4 w-4 mr-2" />
              Component Instructions
            </Button>
            <Button variant="outline" className="w-full justify-start">
              <FileText className="h-4 w-4 mr-2" />
              Scoring Rubric
            </Button>
            <Button variant="outline" className="w-full justify-start">
              <Video className="h-4 w-4 mr-2" />
              Sample Submissions
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5" />
              <span>Success Tips</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start space-x-2">
                <div className="h-1.5 w-1.5 bg-primary rounded-full mt-2" />
                <span>Start early and work consistently</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="h-1.5 w-1.5 bg-primary rounded-full mt-2" />
                <span>Connect with other candidates</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="h-1.5 w-1.5 bg-primary rounded-full mt-2" />
                <span>Use the scoring rubric as your guide</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="h-1.5 w-1.5 bg-primary rounded-full mt-2" />
                <span>Seek feedback from mentors</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};